"use client";

import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { Pagination } from "@2u-landing-pages/components/Pagination";
import { ProductCard } from "@2u-landing-pages/components/ProductCard";
import SkeletonProductCard from "@2u-landing-pages/components/ProductCard/SkeletonProductCard";
import { Tile } from "@2u-landing-pages/components/Tile";
import { Bento } from "@2u-landing-pages/templates/Bento";
import {
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeProduct_cardWithoutUnresolvableLinksResponse,
  TypeTileWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { camelCase, useEqualHeights, useIntersectionObserver } from "@2u-landing-pages/utils";
import { useEqualHeightsOnHighlightChange } from "@2u-landing-pages/providers";
import EventClient from "@2u-landing-pages/utils/event-client";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { Block } from "@contentful/rich-text-types";
import cn from "classnames";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getFilterLabels, getProgramList } from "./Filter/filterAndSort";
import { FilterBar } from "./Filter/FilterBar";
import { CustomCSSProperties, GalleryProps } from "./Gallery.types";
//@ts-ignore
import styles from "./Gallery.module.scss";

interface NetworkInformation {
  effectiveType: string;
}

interface NavigatorWithConnection extends Navigator {
  connection: NetworkInformation;
}

const SORT_OPTIONS = {
  "Distance - Low to High": "proximity:asc",
  "Distance - High to Low": "proximity:desc",
  "Tuition Cost - Low to High": "tuition_cost:asc",
  "Tuition Cost - High to Low": "tuition_cost:desc",
  "Start Date - Closest to Latest": "start_date:asc",
  "Start Date - Latest to Closest": "start_date:desc",
} as const;

type SortOptionValue = (typeof SORT_OPTIONS)[keyof typeof SORT_OPTIONS];

export const Gallery: React.FC<GalleryProps> = ({
  grid,
  show_filters,
  section_heading,
  section_background_color,
  disable_first_card_highlight,
  gallery_card_size,
  filter_variation,
  filter_groups,
  sidebar,
  inspector,
  itemsPerPage = 9,
  lob,
  category,
  geo_sort = true,
  align_card_sections = false,
  mode,
  ...rest
}) => {
  const disableAutoRecommendedFlag = Boolean(disable_first_card_highlight);
  const [filters, setFilters] = useState({});
  const [hasRecommendedCard, setHasRecommendedCard] = useState(false);
  const [programList, setProgramList] = useState<TypeProduct_cardWithoutUnresolvableLinksResponse[]>([]);
  const [programPage, setProgramPage] = useState<TypeProduct_cardWithoutUnresolvableLinksResponse[]>([]);
  const [customStyles, setCustomStyles] = useState<CustomCSSProperties>({});
  const customOrderReceived = useRef(false);
  const consentOverride = useRef(false);
  const [customProgramOrder, setCustomProgramOrder] = useState<string[]>([]);
  const previousFiltersRef = useRef(filters);
  const [locationConsent, setLocationConsent] = useState(false);
  const [sort, setSort] = useState<SortOptionValue>(SORT_OPTIONS["Distance - Low to High"]);
  const gridRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { registerElement, updateHeights, clearElements } = useEqualHeights({
    gallery_card_size,
    align_card_sections,
    mode,
  });

  useEqualHeightsOnHighlightChange(updateHeights);

  const filterLabels = useMemo(() => getFilterLabels(rest), [rest]);

  const productCards = useMemo(() => grid?.fields?.product_cards || [], [grid?.fields?.product_cards]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const consentCheck = params.get("consentCheck");
      consentOverride.current = consentCheck?.toLowerCase() === "false";
    }
  }, []);

  const handleIntersect = useCallback((target: HTMLElement) => {
    const { position, name } = target.dataset;

    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: position,
        text: name,
      },
    });
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });

  useEffect(() => {
    if (["Medium grid", "Small grid"].includes(gallery_card_size)) updateHeights();
  }, [gallery_card_size, updateHeights]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.cohesion) {
      window.cohesion("monarch:done", function (err: object | null, result: any) {
        // Prevents monarch:done from being called multiple times
        if (customOrderReceived.current) return;
        const monarchDecisionResults = result?.source?.trafficFlow?.value?.items?.[0]?.fields?.programs || [];
        customOrderReceived.current = true;
        setCustomProgramOrder(monarchDecisionResults.map((program: any) => program?.sys?.id).filter(Boolean));
      });

      window.cohesion("consent:ready", function () {
        const chsn: any = window?.cohesion;
        const groups = chsn?.consent("list");
        const consentCaptured = Boolean(groups?.find((group: string) => group === "C0002"));

        if (consentCaptured) {
          setLocationConsent(true);
        }
      });

      window.cohesion("consent:changed", function (groups) {
        const consentCaptured = Boolean(groups?.find((group: string) => group === "C0002"));
        if (consentCaptured) {
          setLocationConsent(true);
        }
      });
    }
  }, []);

  // Set isLoading to false after 1 second if consent hasn't been captured
  useEffect(() => {
    const isMounted = { current: true };

    const connection =
      "connection" in navigator ? (navigator as NavigatorWithConnection).connection.effectiveType : "unknown";
    const connectionTimeout = connection === "4g" ? 8000 : 12000;

    const timeoutId = setTimeout(() => {
      if (isMounted.current) setIsLoading(false);
    }, connectionTimeout);

    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const filteredAndSortedPrograms = getProgramList(
      productCards as TypeProduct_cardWithoutUnresolvableLinksResponse[],
      filters,
      sort,
      customProgramOrder,
      consentOverride.current || locationConsent,
      geo_sort
    );

    const pinnedPrograms = filteredAndSortedPrograms.filter((program) =>
      Boolean(program?.fields?.pin_to_bottom)
    ) as TypeProduct_cardWithoutUnresolvableLinksResponse[];
    const unpinnedPrograms = filteredAndSortedPrograms.filter(
      (program) => !Boolean(program?.fields?.pin_to_bottom)
    ) as TypeProduct_cardWithoutUnresolvableLinksResponse[];

    clearElements();

    if (previousFiltersRef.current !== filters) {
      setCurrentPage(1);
      previousFiltersRef.current = filters;
    }

    setProgramList([...unpinnedPrograms, ...pinnedPrograms]);
  }, [
    productCards,
    filters,
    currentPage,
    sort,
    customProgramOrder,
    locationConsent,
    consentOverride,
    geo_sort,
    clearElements,
  ]);

  useEffect(() => {
    const firstCardIsRecommended = programList[0]?.fields?.product_card_flag;

    const customStyleObj: CustomCSSProperties = {};
    if (section_background_color) customStyleObj["--background-color"] = `var(${section_background_color})`;

    if (firstCardIsRecommended && currentPage === 1 && lob === "degree" && !disableAutoRecommendedFlag)
      customStyleObj["--recommended-flag-height"] = "32px";
    if (lob === "execed") customStyleObj["--recommended-flag-padding"] = "48px";
    if (mode === "single-program") customStyleObj["--recommended-flag-padding"] = section_heading ? "32px" : "48px";

    setCustomStyles(customStyleObj);
  }, [programList, currentPage, section_background_color, lob, mode, section_heading, disableAutoRecommendedFlag]);

  useEffect(() => {
    const paginatedPrograms = programList?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    setProgramPage(paginatedPrograms);

    setHasRecommendedCard(
      Boolean(
        !disableAutoRecommendedFlag &&
          currentPage === 1 &&
          paginatedPrograms?.[0]?.fields?.product_card_flag,
      ),
    );

    if (consentOverride.current || locationConsent || customProgramOrder.length > 0) {
      setTimeout(() => setIsLoading(false), 250);
    }
  }, [programList, currentPage, itemsPerPage, updateHeights, consentOverride, locationConsent, customProgramOrder, disableAutoRecommendedFlag]);

  useLayoutEffect(() => {
    // Update heights after all DOM updates are done
    updateHeights();
  }, [programPage, updateHeights]);

  return (
    <div className={`gallery-container ${styles.galleryContainer}`} style={customStyles}>
      <div
        className={cn(styles.galleryWrapper, {
          [styles.hasFilter]: show_filters,
        })}
      >
        {section_heading && (
          <div className={cn(styles.galleryHeader, { [styles.showFilters]: show_filters })}>
            <ContentLayout
              key={section_heading?.sys?.id}
              content={section_heading as TypeContent_fullWithoutUnresolvableLinksResponse}
            />
          </div>
        )}
        {/* Filters and Sort
       ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */}
        {show_filters && (
          <FilterBar
            programs={productCards as unknown as TypeProduct_cardWithoutUnresolvableLinksResponse[]}
            filteredPrograms={programList as unknown as TypeProduct_cardWithoutUnresolvableLinksResponse[]}
            filters={filters}
            setFilters={setFilters}
            numResults={programList.length}
            filterGroups={filter_groups}
            filterLabels={filterLabels}
            noRecommendedCard={!hasRecommendedCard}
            variation={filter_variation}
            backgroundColor={section_background_color}
          />
        )}

        <main className={`${styles.gallery} ${(sidebar || []).length > 0 ? styles.hasSidebar : styles.noSidebar}`}>
          {/* Product Card Gallery
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */}
          <div
            ref={gridRef}
            className={`${styles.galleryProducts} ${gallery_card_size ? styles[camelCase(gallery_card_size)] : ""}`}
          >
            {isLoading &&
              Array.from({ length: itemsPerPage }).map((_, i) => (
                <SkeletonProductCard key={`skeleton-${i}`} show={isLoading} />
              ))}

            {!isLoading &&
              programPage.map((program, index) => {
                if (!program || !program.fields || !program.sys?.id) return null;
                const uniqueKey = `${program.sys.id}-${index}`;

                return (
                  <ProductCard
                    key={uniqueKey}
                    ref={(el: HTMLDivElement | null) => {
                      if (el) {
                        el.dataset.location = "Gallery";
                        el.dataset.position = index.toString();
                        addObserver(el);
                        registerElement(uniqueKey, el);
                      }
                    }}
                    currentPage={currentPage}
                    index={index}
                    product={program}
                    lob={lob}
                    category={category}
                    position={index + 1 + (currentPage - 1) * itemsPerPage}
                    location="Gallery"
                    galleryCardSize={gallery_card_size}
                    mode={mode}
                    standalone={false}
                    disableRecommendedFlag={disableAutoRecommendedFlag}
                  />
                );
              })}
          </div>

          {/* Sidebar
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */}
          {(sidebar || []).length > 0 && (
            <div className={styles.gallerySidebar}>
              {(sidebar || []).map((item, index) => {
                if (!item) return null;
                const contentType = item?.sys?.contentType?.sys?.id;
                const id = item?.sys?.id;
                if (!id) return null;

                const uniqueKey = `${id}_${index}`;

                switch (contentType) {
                  case "content_full":
                    const { fields: { heading: contentHeading, subhead: contentSubhead } = {} } =
                      item as TypeContent_fullWithoutUnresolvableLinksResponse;
                    const contentHeadingText = contentHeading
                      ? documentToPlainTextString(contentHeading as unknown as Block)
                      : "";
                    const contentSubheadText = contentSubhead
                      ? documentToPlainTextString(contentSubhead as unknown as Block)
                      : "";
                    return (
                      <div
                        key={uniqueKey}
                        ref={(element) => {
                          if (element) addObserver(element);
                        }}
                        data-location="Sidebar"
                        data-position="Sidebar"
                        data-name={contentHeadingText || contentSubheadText}
                      >
                        <ContentLayout content={item as TypeContent_fullWithoutUnresolvableLinksResponse} />
                      </div>
                    );
                  case "tile":
                    const { fields: { heading: tileHeading, subhead: tileSubhead } = {} } =
                      item as TypeTileWithoutUnresolvableLinksResponse;
                    const tileHeadingText = tileHeading
                      ? documentToPlainTextString(tileHeading as unknown as Block)
                      : "";
                    const tileSubheadText = tileSubhead
                      ? documentToPlainTextString(tileSubhead as unknown as Block)
                      : "";
                    return (
                      <div
                        key={uniqueKey}
                        ref={(element) => {
                          if (element) addObserver(element);
                        }}
                        data-location="Sidebar"
                        data-position="Sidebar"
                        data-name={tileHeadingText || tileSubheadText}
                      >
                        <Tile
                          className={styles.tile}
                          content={item as TypeTileWithoutUnresolvableLinksResponse}
                          padded
                          heading={{ variant: "heading-lg" }}
                          body={{ variant: "body-sm" }}
                          mobileOrientation="vertical"
                          desktopOrientation="vertical"
                        />
                      </div>
                    );
                  case "bento":
                    const { fields: { section_heading } = {} } = item as TypeBentoWithoutUnresolvableLinksResponse;
                    const bentoHeadingText = section_heading
                      ? documentToPlainTextString(section_heading?.fields?.heading as unknown as Block)
                      : "";
                    return (
                      <div
                        key={uniqueKey}
                        ref={(element) => {
                          if (element) addObserver(element);
                        }}
                        data-location="Sidebar"
                        data-position="Sidebar"
                        data-name={bentoHeadingText}
                      >
                        <Bento
                          id={id}
                          {...((item as TypeBentoWithoutUnresolvableLinksResponse).fields || {})}
                          inspector={inspector}
                        />
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          )}
        </main>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(programList?.length / itemsPerPage)}
        onPageChange={(page) => {
          setCurrentPage(page);
          const galleryContainer = document.querySelector(".gallery-container");
          if (galleryContainer) {
            galleryContainer.scrollIntoView({ behavior: "smooth" });
          }
        }}
      />
    </div>
  );
};
